// ── Enums ──────

export enum FreightMode {
  Air = 'air',
  OceanLCL = 'ocean-lcl',
  Courier = 'courier',
}

export enum DimensionUnit {
  Cm = 'cm',
  Mm = 'mm',
  M = 'm',
  In = 'in',
  Ft = 'ft',
}

export enum WeightUnit {
  Kg = 'kg',
  Lb = 'lb',
}

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
  System = 'system',
}

export enum WeightBasis {
  Actual = 'actual',
  Dimensional = 'dimensional',
  Equal = 'equal',
}

// ── Package Line (Input) ──────

export interface PackageLine {
  id: string;
  length: number;
  width: number;
  height: number;
  dimUnit: DimensionUnit;
  weight: number;
  weightUnit: WeightUnit;
  quantity: number;
}

// ── Shipment Input ──────

export interface ShipmentInput {
  packages: PackageLine[];
  mode: FreightMode;
  dimDivisor: number;
  overrideDivisor: boolean;
}

// ── Single Line Result ──────

export interface LineResult {
  packageId: string;
  cbm: number;
  cbmPerPiece: number;
  dimWeight: number;
  actualWeight: number;
  chargeableWeight: number;
  weightBasis: WeightBasis;
  quantity: number;
}

// ── Shipment Summary ──────

export interface ShipmentSummary {
  totalCBM: number;
  totalPackages: number;
  grossWeightKg: number;
  dimWeightKg: number;
  chargeableWeightKg: number;
  weightBasis: WeightBasis;
  lineResults: LineResult[];
  cargoDensityKgPerCBM: number;
}

// ── Container Specs ──────

export interface ContainerSpec {
  type: string;
  internalLength: number;
  internalWidth: number;
  internalHeight: number;
  maxPayloadKg: number;
  volumeCBM: number;
}

export interface ContainerFitResult {
  containerType: string;
  containerCount: number;
  volumeUtilization: number;
  weightUtilization: number;
  usedCBM: number;
  availableCBM: number;
}

// ── Freight Cost ──────

export interface Accessorial {
  name: string;
  cost: number;
}

export interface FreightCostInput {
  ratePerKg: number;
  ratePerCBM: number;
  ratePerRevenueTon: number;
  ratePerContainer: number;
  accessorials: Accessorial[];
  currency: string;
}

export interface FreightCostResult {
  baseCost: number;
  accessorialCost: number;
  totalCost: number;
  currency: string;
  rateUsed: number;
  chargeableUnit: string;
  chargeableAmount: number;
}

// ── Pallet Specs ──────

export interface PalletSpec {
  type: string;
  length: number;
  width: number;
  maxStackHeight: number;
  maxWeightKg: number;
}

export interface PalletFitResult {
  palletType: string;
  cartonsPerLayer: number;
  layers: number;
  totalCartons: number;
  palletCount: number;
  volumeUtilization: number;
}

// ── Saved Calculation ──────

export interface SavedCalculation {
  id: string;
  name: string;
  timestamp: string;
  shipment: ShipmentInput;
  summary: ShipmentSummary;
}

// ── User Preferences ──────

export interface UserPreferences {
  defaultDimUnit: DimensionUnit;
  defaultWeightUnit: WeightUnit;
  defaultFreightMode: FreightMode;
  defaultDimDivisor: number;
  theme: ThemeMode;
  currency: string;
  defaultRatePerKg: number;
  defaultRatePerCBM: number;
  showDemoOption: boolean;
}

// ── Validation ──────

export interface ValidationError {
  packageId: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

// ── Glossary ──────

export interface GlossaryTerm {
  term: string;
  definition: string;
}

// ── Package Template ──────

export interface PackageTemplate {
  name: string;
  length: number;
  width: number;
  height: number;
  dimUnit: DimensionUnit;
}

// ── Tab Type ──────

export type AppTab = 'calculator' | 'optimization' | 'reference' | 'history';
